import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  Balances,
  EditedRecord,
  PendingTimeOffRequest,
  RequestTypes,
  TimeOffRequest
} from '../../../models/TimeOffModels';
import { Select, Selector, Store } from '@ngxs/store';
import { CompaniesService } from '../../../services/companies.service';
import { EmployeePTO, PTOConfig } from '../../../models/paid-time-off';
import { DateRange } from '../../../models/date-range';
import { TimeOffRequestService } from '../../../services/time-off-request.service';
import {
  formatDatePickerValue,
  parseDatePickerValue,
  parseTimeFromDate,
  parseTimeString
} from '../../../utils/datepicker-value-parser';
import {
  dateRangeComponentValidator,
  timeRangeNotEqualValidator,
  timeRangeValidator
} from '../../../utils/dateRangeValidator';
import { GetDaysBetweenTwoDates, GetHoursBetweenTwoTimes } from '../../../utils/dateUtils';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { AdminService } from '../../../services/admin.service';
import { map } from 'rxjs/operators';
import { PayrollService } from '../../../services/payroll.service';
import { PayDateInfo } from '../../../models/payroll';
import { EnumService } from '../../../services/enum.service';
import { ClientSelectionState } from '../../../state/ClientSelectionState/ClientSelection.state';
import { ResetForm } from '@ngxs/form-plugin';
import { TimeOffRequestsState } from '../../../state/TimeOffRequestsState/TimeOffRequests.state';
import { PeopleService } from '../../../services/people.service';
import { ModalService } from '../../../services/modal.service';

@Component({
  selector: 'lib-employee-time-off-request-modal',
  templateUrl: './employee-time-off-request-modal.component.html',
  styleUrls: ['./employee-time-off-request-modal.component.css'],
  standalone: false
})
export class EmployeeTimeOffRequestModalComponent implements OnInit {
  @Input()
  public personId: number;

  @Input()
  public existingModel: PendingTimeOffRequest | null = null;

  @Input()
  public defaultDay: Date | null = null;

  public form: FormGroup;
  @Select(TimeOffRequestsState.isDateRangeWeekend)
  public isWeekend: Observable<boolean>;

  @Output()
  public modalClosed: EventEmitter<any> = new EventEmitter<any>();

  public balanceIsNegative: Observable<boolean>;
  public negativeBalanceAllow = false;

  public timeOffPlans: PTOConfig[] = [];
  public employeePtos: EmployeePTO[] = [];
  public displayPtos: PTOConfigDisableable[] = [];
  public chosenPlanIdObs = null;
  public balances: Balances[] = [];
  public currentRequestHoursObs: BehaviorSubject<number> | null = null;
  public showHolidayMessage = false;
  @Select(TimeOffRequestsState.isDateInPast)
  public showDateMessage: Observable<boolean>;

  public earliestDate: { year: number; month: number; day: number };
  public now: { year: number; month: number; day: number };

  constructor(
    private fb: FormBuilder,
    private companiesService: CompaniesService,
    private timeOffService: TimeOffRequestService,
    private adminService: AdminService,
    private payrollService: PayrollService,
    private peopleService: PeopleService,
    private store: Store,
    private modalService: ModalService
  ) {}

  get partialDayHours() {
    if (this.form.get('requestType').value != RequestTypes.PartialDay) {
      return 0;
    }
    if (this.form.get('startTime').value == null || this.form.get('endTime').value == null) {
      return 0;
    }
    return GetHoursBetweenTwoTimes(
      this.form.get('startTime').value,
      this.form.get('endTime').value
    );
  }

  ngOnInit() {
    this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
      this.now = formatDatePickerValue(new Date());
      this.companiesService.getPTOConfigs(code).subscribe(ptos => {
        this.timeOffPlans = ptos;
        this.timeOffService.getBalances(code, this.personId).subscribe(balances => {
          this.balances = balances;
        });
        this.balanceIsNegative = this.store
          .select(TimeOffRequestsState.getRemainingBalance(this.personId))
          .pipe(
            map(bal => {
              return bal < 0;
            })
          );
        this.peopleService.getEmployeePTOs(code, this.personId).subscribe(employeePtos => {
          this.employeePtos = employeePtos;
          if (this.existingModel == null) {
            this.displayPtos = this.timeOffPlans
              .filter(config => {
                const employeePto = this.employeePtos.find(x => x.IsActive && x.Id == config.Id);
                return config.IsActive && config.IsRequestAllowed && employeePto != null;
              })
              .map(makeDisableable);
          } else {
            this.displayPtos = this.timeOffPlans.map(makeDisableable).map(config => {
              const employeePto = this.employeePtos.find(x => x.IsActive && x.Id == config.Id);
              return {
                ...config,
                disabled: !config.IsActive || !config.IsRequestAllowed || employeePto == null
              };
            });
          }
        });

        this.companiesService.getLocations(code).subscribe(locs => {
          const nextPayDateObs = [];
          locs.forEach(loc => {
            loc.Calendars.forEach(cal => {
              nextPayDateObs.push(this.payrollService.getNextPayDate(code, loc.Id, cal.CalendarId));
            });
          });
          forkJoin(nextPayDateObs).subscribe((nextPayDates: PayDateInfo[]) => {
            const sorted = nextPayDates.sort((a, b) => {
              if (a.PeriodBegin < b.PeriodBegin) return -1;
              if (a.PeriodBegin > b.PeriodBegin) return 1;
              return 0;
            });
            this.earliestDate = formatDatePickerValue(sorted[0].PeriodBegin);
            this._buildForm();
          });
        });
      });
    });
  }

  public closeModal() {
    this.modalClosed.emit();
  }

  public cancelRequest() {
    const test = this.modalService.confirmModalCallback.subscribe(result => {
      if (result) {
        this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
          this.timeOffService
            .cancelRequest(code, this.personId, this.existingModel.RequestId)
            .subscribe(_ => {
              this.closeModal();
            });
        });
      }
      test.unsubscribe();
    });
    this.modalService.showConfirmModal(
      'Are you sure you want to cancel this request?',
      'No',
      'Yes',
      'Confirm Cancellation'
    );
  }

  public submitForm() {
    const formVal = this.form.value;
    const plan = this.timeOffPlans.find(x => x.Id == formVal.timeOffType);

    const model: TimeOffRequest = {
      TimeOffType: formVal.timeOffType,
      TimeOffTypeName: plan.Name,
      TimeOffRequestType: formVal.requestType,
      StartDate: formVal.startDate != null ? parseDatePickerValue(formVal.startDate) : null,
      Hours: formVal.hours,
      StartTime: null,
      EndTime: null,
      DateRange:
        formVal.dateRange != null
          ? { Start: formVal.dateRange.start, End: formVal.dateRange.end }
          : null,
      EmployeeComments: formVal.employeeComments,
      ClientComments: null
    };
    model.StartTime =
      formVal.startTime != null ? parseTimeString(formVal.startTime, model.StartDate) : null;
    model.EndTime =
      formVal.startTime != null ? parseTimeString(formVal.endTime, model.StartDate) : null;
    this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
      if (this.existingModel == null) {
        this.timeOffService.submitRequest(code, this.personId, model).subscribe(_ => {
          this.closeModal();
        });
      } else {
        this.timeOffService
          .updateRequest(code, this.personId, this.existingModel.RequestId, model)
          .subscribe(_ => {
            this.closeModal();
          });
      }
    });
  }

  private _buildForm() {
    let startDate = null;
    if (this.existingModel != null) {
      const plan = this.timeOffPlans.find(x => x.Id == this.existingModel.Request.TimeOffType);

      this.negativeBalanceAllow = plan.RequestNegativeBalRule == 0;
      startDate = this.existingModel.Request.StartDate;
    } else {
      const today = new Date();
      if (today < parseDatePickerValue(this.earliestDate)) {
        startDate = this.defaultDay ?? parseDatePickerValue(this.earliestDate);
      } else {
        startDate = this.defaultDay ?? new Date();
      }
    }
    this.form = this.fb.group({
      requestId: this.fb.control(this.existingModel?.RequestId),
      timeOffType: this.fb.control(this.existingModel?.Request?.TimeOffType, [Validators.required]),
      requestType: this.fb.control(
        this.existingModel?.Request?.TimeOffRequestType ?? RequestTypes.SingleDay,
        [Validators.required]
      ),
      startDate: this.fb.control(startDate != null ? formatDatePickerValue(startDate) : null, [
        Validators.required
      ]),
      hours: this.fb.control(this.existingModel?.Request?.Hours, [
        Validators.required,
        Validators.min(0)
      ]),
      employeeComments: this.fb.control(this.existingModel?.Request?.EmployeeComments),
      startTime: this.fb.control(
        this.existingModel?.Request?.StartTime != null
          ? parseTimeFromDate(this.existingModel.Request.StartTime)
          : null
      ),
      endTime: this.fb.control(
        this.existingModel?.Request?.EndTime != null
          ? parseTimeFromDate(this.existingModel?.Request?.EndTime)
          : null
      ),
      dateRange: this.fb.control(
        {
          start: this.existingModel?.Request?.DateRange?.Start,
          end: this.existingModel?.Request?.DateRange?.End
        },
        [dateRangeComponentValidator]
      )
    });
    this.store.dispatch(
      new ResetForm({ path: 'timeOffRequests.NewRequestForm', value: this.form.value })
    );
    this.chosenPlanIdObs = this.form.get('timeOffType').valueChanges;
    this.currentRequestHoursObs = new BehaviorSubject(this._initCurrentRequestHours());
    this.form.get('hours').valueChanges.subscribe(val => {
      const hours = this._initCurrentRequestHours();
      this.currentRequestHoursObs.next(hours);
    });
    this.form.get('startTime').valueChanges.subscribe(val => {
      const hours = this._initCurrentRequestHours();
      this.currentRequestHoursObs.next(hours);
    });
    this.form.get('endTime').valueChanges.subscribe(val => {
      const hours = this._initCurrentRequestHours();
      this.currentRequestHoursObs.next(hours);
    });
    this.form.setValidators([
      timeRangeValidator('startTime', 'endTime'),
      timeRangeNotEqualValidator('startTime', 'endTime')
    ]);
    this.form.updateValueAndValidity();
    this._setFormValidation(this.form.get('requestType').value, true);

    if (this.existingModel != null) {
      // check holidays
      if (this.existingModel.Request.TimeOffRequestType == RequestTypes.MultipleDays) {
        const start = this.existingModel.Request.DateRange.Start;
        const end = this.existingModel.Request.DateRange.End;
        this.adminService
          .getHolidays(start, end)
          .pipe(
            map(x => {
              return x.length > 0;
            })
          )
          .subscribe(result => {
            this.showHolidayMessage = result;
          });
      } else {
        let parsed = this.existingModel.Request.StartDate;
        if (typeof parsed == 'string') {
          parsed = new Date(parsed);
        }
        const start = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() - 1);
        const end = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() + 1);
        this.adminService
          .getHolidays(start, end)
          .pipe(
            map(x => {
              return x.length > 0;
            })
          )
          .subscribe(result => {
            this.showHolidayMessage = result;
          });
      }
    }

    this.form.get('requestType').valueChanges.subscribe(val => {
      this._setFormValidation(val);
    });
    this.form.get('startDate').valueChanges.subscribe(startDate => {
      if (startDate == null) {
        return;
      }
      const parsed = parseDatePickerValue(startDate);
      const start = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() - 1);
      const end = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() + 1);
      this.adminService
        .getHolidays(start, end)
        .pipe(
          map(x => {
            return x.length > 0;
          })
        )
        .subscribe(result => {
          this.showHolidayMessage = result;
        });

      const parsedNow = parseDatePickerValue(this.now);
    });
    this.form.get('timeOffType').valueChanges.subscribe(val => {
      const plan = this.timeOffPlans.find(x => x.Id == val);
      if (plan == null) return;
      this.negativeBalanceAllow = plan.RequestNegativeBalRule == 0;
    });
    this.form.get('dateRange').valueChanges.subscribe(range => {
      if (range?.start == null || range?.end == null) {
        return;
      }
      const parsedEarliestDate = parseDatePickerValue(this.earliestDate);
      const parsedNow = parseDatePickerValue(this.now);
      const start = range.start;
      const end = range.end;

      this.adminService
        .getHolidays(start, end)
        .pipe(
          map(x => {
            return x.length > 0;
          })
        )
        .subscribe(result => {
          this.showHolidayMessage = result;
        });
    });
  }

  private _initCurrentRequestHours() {
    const requestType: RequestTypes = this.form.get('requestType').value;
    switch (requestType) {
      case RequestTypes.PartialDay:
        return GetHoursBetweenTwoTimes(
          this.form.get('startTime').value,
          this.form.get('endTime').value
        );
      case RequestTypes.SingleDay:
        return this.form.get('hours').value;
      case RequestTypes.MultipleDays:
        return (
          this.form.get('hours').value *
          GetDaysBetweenTwoDates(
            this.form.get('dateRange').value?.start,
            this.form.get('dateRange').value?.end
          )
        );
    }
  }

  private _setFormValidation(val: RequestTypes, initial = false) {
    if (!initial) {
      this.form.get('startDate').reset();
      this.form.get('hours').reset();
      this.form.get('startTime').reset();
      this.form.get('endTime').reset();
      this.form.get('dateRange').reset();
    }
    if (val == RequestTypes.SingleDay) {
      this.form.get('startDate').setValidators(Validators.required);
      this.form.get('startDate').updateValueAndValidity();
      this.form
        .get('hours')
        .setValidators([Validators.required, Validators.min(1), Validators.max(24)]);
      this.form.get('hours').updateValueAndValidity();
      this.form.get('startTime').clearValidators();
      this.form.get('startTime').updateValueAndValidity();
      this.form.get('endTime').clearValidators();
      this.form.get('endTime').updateValueAndValidity();
      this.form.get('dateRange').clearValidators();
      this.form.get('dateRange').updateValueAndValidity();
    } else if (val == RequestTypes.PartialDay) {
      this.form.get('startDate').setValidators(Validators.required);
      this.form.get('startDate').updateValueAndValidity();
      this.form.get('hours').clearValidators();
      this.form.get('hours').updateValueAndValidity();
      this.form.get('startTime').setValidators(Validators.required);
      this.form.get('startTime').updateValueAndValidity();
      this.form.get('endTime').setValidators(Validators.required);
      this.form.get('endTime').updateValueAndValidity();
      this.form.get('dateRange').clearValidators();
      this.form.get('dateRange').updateValueAndValidity();
    } else if (val == RequestTypes.MultipleDays) {
      this.form
        .get('hours')
        .setValidators([Validators.required, Validators.min(1), Validators.max(24)]);
      this.form.get('hours').updateValueAndValidity();
      this.form.get('dateRange').setValidators([Validators.required, dateRangeComponentValidator]);
      this.form.get('dateRange').updateValueAndValidity();
      this.form.get('startDate').clearValidators();
      this.form.get('startDate').updateValueAndValidity();
      this.form.get('startTime').clearValidators();
      this.form.get('startTime').updateValueAndValidity();
      this.form.get('endTime').clearValidators();
      this.form.get('endTime').updateValueAndValidity();
    }
    this.form.updateValueAndValidity();
  }

  protected readonly RequestTypes = RequestTypes;
}

interface PTOConfigDisableable extends PTOConfig {
  disabled: boolean;
}

function makeDisableable(config: PTOConfig): PTOConfigDisableable {
  return {
    ...config,
    disabled: false
  };
}
